import React, { useState, useEffect } from 'react';
import { Typography, Form, Input, Button, Switch, Card, message, Upload, List, Image } from 'antd';
import { UploadOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import API_ENDPOINTS, { API_CONFIG } from '../config/api';

const { Title } = Typography;
const { TextArea } = Input;

const Settings = () => {
  const [sliderImages, setSliderImages] = useState([]);
  const [logoFile, setLogoFile] = useState(null);

  // Add the styles useEffect inside the component
  useEffect(() => {
    const styles = `
      .logo-edit-overlay {
        opacity: 0;
      }
      .logo-edit-overlay:hover {
        opacity: 1 !important;
      }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchSliderImages();
    fetchLogoImage();
  }, []);

  const fetchSliderImages = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.SLIDER);
      const data = await response.json();
      if (data.success) {
        const formattedImages = data.images.map(img => ({
          uid: img.filename,
          name: img.filename,
          status: 'done',
          url: API_CONFIG.getImageUrl(img.filepath)
        }));
        setSliderImages(formattedImages);
      }
    } catch (error) {
      message.error('Failed to fetch slider images');
    }
  };

  const fetchLogoImage = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.LOGO);
      const data = await response.json();
      if (data.success) {
        setLogoFile({
          uid: data.logo.filename,
          name: data.logo.filename,
          status: 'done',
          url: API_CONFIG.getImageUrl(data.logo.filepath)
        });
      }
    } catch (error) {
      message.error('Failed to fetch logo');
    }
  };

  const handleSliderRemove = async (file) => {
    try {
      const response = await fetch(API_ENDPOINTS.SLIDER, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename: file.name })
      });
      
      const data = await response.json();
      if (data.success) {
        setSliderImages(prev => prev.filter(item => item.uid !== file.uid));
        message.success('Image deleted successfully');
      } else {
        message.error(data.error || 'Failed to delete image');
      }
    } catch (error) {
      message.error('Failed to delete image');
    }
  };

  const handleSliderUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('image', file);  // Changed to 'image' to match backend expectation

    try {
      const response = await fetch(API_ENDPOINTS.SLIDER, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      console.log('Upload response:', result);

      if (result.success) {
        onSuccess(result);
        message.success('Image uploaded successfully');
        fetchSliderImages(); // Refresh the images list
      } else {
        onError(result.error || 'Upload failed');
        message.error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      onError(error);
      message.error('Failed to upload image');
    }
  };

  const handleSettingsSubmit = async (values) => {
    try {
      // Here you would handle the general settings submission
      // This should be a separate API endpoint for saving website settings
      console.log('Settings to save:', values);
      message.success('Settings saved successfully');
    } catch (error) {
      console.error('Settings save error:', error);
      message.error('Failed to save settings');
    }
  };

  const handleLogoSubmit = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await fetch(API_ENDPOINTS.LOGO, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (result.status === 'success') {
        onSuccess(result);
        message.success('Logo updated successfully');
        fetchLogoImage();
      } else {
        onError(result.message || 'Failed to update logo');
        message.error(result.message || 'Failed to update logo');
      }
    } catch (error) {
      onError(error);
      message.error('Failed to save logo');
    }
  };

  const handleSliderChange = ({ fileList }) => {
    // Update only the successfully uploaded files
    const successFiles = fileList.filter(file => file.status === 'done');
    setSliderImages(successFiles);
  };

  const handleLogoChange = ({ fileList }) => {
    if (fileList.length > 0) {
      const lastFile = fileList[fileList.length - 1];
      if (lastFile.status === 'done') {
        setLogoFile(lastFile);
      }
    } else {
      setLogoFile(null);
    }
  };

  return (
    <div>
      <Title level={2}>Website Settings</Title>
      <Card>
        <Form
          name="settings"
          layout="vertical"
          initialValues={{ 
            siteName: 'My Website',
            siteDescription: 'Welcome to my website',
            contactEmail: 'contact@example.com',
            phoneNumber: '+1 234 567 8900',
            address: '',
            socialMedia: {
              facebook: '',
              twitter: '',
              instagram: ''
            }
          }}
          onFinish={handleSettingsSubmit}
        >
          {/* Logo Upload Section */}
          <Form.Item
            name="logo"
            label="Website Logo"
            extra="Supported formats: JPG, JPEG, PNG, GIF. Max size: 2MB"
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Upload
                name="logo"
                listType="picture-card"
                showUploadList={false}
                onChange={handleLogoChange}
                customRequest={handleLogoSubmit}
                maxCount={1}
                accept="image/*"
              >
                {logoFile ? (
                  <div style={{ position: 'relative' }}>
                    <Image
                      src={logoFile.url}
                      alt="Logo"
                      width={100}
                      height={100}
                      style={{ objectFit: 'contain' }}
                      preview={false}
                    />
                    <div className="logo-edit-overlay">
                      <EditOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload Logo</div>
                  </div>
                )}
              </Upload>
            </div>
          </Form.Item>

          {/* Slider Images Section */}
          <Form.Item
            name="sliderImages"
            label="Slider Images"
            extra="Supported formats: JPG, JPEG, PNG, GIF. Max size: 5MB"
          >
            <Upload
              name="image"
              listType="picture-card"
              fileList={sliderImages}
              onChange={handleSliderChange}
              onRemove={handleSliderRemove}
              customRequest={handleSliderUpload}  // Changed to new upload handler
              multiple={true}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>

          {/* Preview Current Slider Images */}
          <Form.Item label="Current Slider Images">
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {sliderImages.map(image => (
                <div key={image.uid} style={{ position: 'relative' }}>
                  <Image
                    src={image.url}
                    alt={image.name}
                    width={200}
                    height={150}
                    style={{ objectFit: 'cover' }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleSliderRemove(image)}
                    style={{
                      position: 'absolute',
                      top: 5,
                      right: 5,
                      background: 'rgba(255, 255, 255, 0.8)'
                    }}
                  />
                </div>
              ))}
            </div>
          </Form.Item>     
        </Form>
      </Card>
    </div>
  );
};

export default Settings; 